import { parseRunCodeStrict, type RunCodeStrictParseResult } from '@chatsdet/browser-core';
import type { TraceAction, TraceViewport } from '@chatsdet/trace-tools';
import type { AssertionSpec, CachedAction, PlaybookStep, SelectorStrategy } from '@chatsdet/types';
import type { MacroResolutionLog } from '../../macros/types.js';
import type { StoredCompiledEntry } from '../../types.js';
import type { HeadlessDomSession } from '../headless-session.js';
import type { LocatorVerifier } from '../locator-verifier.js';
import type { SubstrateMaterializer } from '../substrate-materializer.js';
import {
  compareStepArtifacts,
  derivePredicateRank,
  type StepArtifactQuality,
} from './comparator.js';
import type {
  GroundTruthReasonCode,
  LinkReasonCode,
  NodeDecisionLog,
  ShadowCandidate,
  StepDecision,
  StepDecisionReasonCode,
} from './decision-log.js';
import type {
  LinkResult,
  StrictParsedRunCode,
} from './step-linker.js';
import { linkSignalsByStepMap } from './step-linker.js';
import type {
  BuildCachePlaybookOptions,
  CarriedCachedAction,
} from '../playbook.js';
import type { PlannedBrowserAction } from '../action-synthesizer.js';
import type { NodeSegment } from '../trace-node-segmenter.js';
import {
  authorAndVerify,
  discoverTarget,
  type AuthorAndVerifyOptions,
  type AuthorAndVerifyResult,
  type DiscoverTargetOptions,
  type DiscoverTargetResult,
  type PrimaryTarget,
} from './ground-truth.js';

export interface DeterministicCacheRowLike {
  actionsJson?: readonly unknown[];
  compiledEntriesJson?: readonly StoredCompiledEntry[] | null;
  playbookJson?: readonly PlaybookStep[] | null;
  compilationMeta?: { compilationScore?: number } | Record<string, unknown> | null;
  playbookSuccessStreak?: number | null;
  createdFromRunId?: string | null;
}

export interface DeterministicPlannedArtifact {
  stepOrder: number;
  plannedAction: PlannedBrowserAction;
  quality: StepArtifactQuality;
  layer?: 1 | 2 | 3 | 4;
  reason?: string;
  signals?: string[];
}

export interface DeterministicStepGap {
  stepOrder: number;
  reason: StepDecisionReasonCode;
  detail?: string;
  signals?: string[];
}

export interface DeterministicPlaybookComposition {
  playbook: Omit<BuildCachePlaybookOptions, 'nodeId'>;
  decisionLog: NodeDecisionLog;
  counts: {
    improved: number;
    incumbent: number;
    carry: number;
    gap: number;
    pending: number;
    shadowCandidates: number;
    shadowCandidateFailures: number;
  };
}

export interface DeterministicNodeInputPreparation {
  rowBridgeRow: DeterministicCacheRowLike | null;
  incumbentRow: DeterministicCacheRowLike | null;
  rowBridge?: { compiledEntries: StoredCompiledEntry[]; actions: CachedAction[] };
  incumbentArtifacts: Map<number, CachedAction>;
  incumbentQualities: Map<number, StepArtifactQuality>;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  segmentActions: Array<{ index: number; action: TraceAction }>;
}

export interface DeterministicCarryThroughSource {
  playbookSteps: PlaybookStep[];
  assertionSpecsByStepOrder?: Record<number, AssertionSpec>;
}

export interface DeterministicAuthoredArtifacts {
  artifactsByStep: Map<number, DeterministicPlannedArtifact>;
  gaps: DeterministicStepGap[];
}

export interface DeterministicAuthoringDeps {
  discoverTarget?: (opts: DiscoverTargetOptions) => Promise<DiscoverTargetResult>;
  authorAndVerify?: (opts: AuthorAndVerifyOptions) => Promise<AuthorAndVerifyResult>;
}

export function prepareDeterministicNodeInputs(args: {
  nodeId: string;
  playbookSteps: PlaybookStep[];
  sourceRunId: string;
  cacheRows: readonly DeterministicCacheRowLike[];
  segments: readonly NodeSegment[];
  segmentNodeIds: ReadonlyArray<string | null>;
}): DeterministicNodeInputPreparation {
  const rowBridgeRow =
    args.cacheRows.find((row) => row.createdFromRunId === args.sourceRunId) ?? null;
  const incumbentRow = pickBestValidCacheRow(args.cacheRows);
  const rowBridge = rowBridgeRow ? buildRowBridge(rowBridgeRow) : undefined;
  const incumbentArtifacts = incumbentRow
    ? buildIncumbentArtifactsByStep(incumbentRow, args.playbookSteps)
    : new Map<number, CachedAction>();
  const incumbentQualities = buildIncumbentQualitiesByStep(
    incumbentArtifacts,
    incumbentRow?.playbookSuccessStreak ?? 0
  );
  const parsedRunCodeBySteps = buildParsedRunCodeBySteps({
    steps: args.playbookSteps,
    cacheRow: rowBridgeRow ?? incumbentRow,
  });
  const segmentActions = collectSegmentActionsForNode({
    nodeId: args.nodeId,
    segments: args.segments,
    segmentNodeIds: args.segmentNodeIds,
  });

  return {
    rowBridgeRow,
    incumbentRow,
    ...(rowBridge ? { rowBridge } : {}),
    incumbentArtifacts,
    incumbentQualities,
    parsedRunCodeBySteps,
    segmentActions,
  };
}

export function adaptParseRunCodeStrictResult(
  parsed: RunCodeStrictParseResult
): StrictParsedRunCode {
  return {
    interactions: parsed.interactions
      .map((interaction) => {
        const kind = strictKindForParsedInteraction(interaction);
        if (!kind) return null;
        return {
          kind,
          ...(interaction.elementDescription ? { locatorChain: interaction.elementDescription } : {}),
          ...(interaction.value !== undefined ? { value: interaction.value } : {}),
          ...(interaction.key !== undefined ? { key: interaction.key } : {}),
          ...(interaction.url !== undefined ? { url: interaction.url } : {}),
          underControlFlow: interaction.provenance.underControlFlow,
          sourceText: interaction.provenance.statementSource || interaction.source,
        };
      })
      .filter(
        (interaction): interaction is StrictParsedRunCode['interactions'][number] =>
          interaction !== null
      ),
    hasControlFlow: parsed.hasControlFlow,
    unclassifiedCount: parsed.unclassifiedCount,
  };
}

export function formatStructuredGapReason(args: { code: string; detail?: string }): string {
  const detail = args.detail?.trim() || 'no detail';
  return `${args.code}: ${detail}`;
}

function mergedSignals(...groups: Array<readonly string[] | undefined>): string[] | undefined {
  const signals = new Set<string>();
  for (const group of groups) {
    for (const signal of group ?? []) signals.add(signal);
  }
  return signals.size > 0 ? [...signals].sort() : undefined;
}

export function composeDeterministicPlaybookPlan(args: {
  nodeId: string;
  playbookSteps: PlaybookStep[];
  candidateArtifactsByStep: Map<number, DeterministicPlannedArtifact>;
  carryThroughActionsByStep: Map<number, PlannedBrowserAction>;
  incumbentActionsByStep: Map<number, CachedAction>;
  incumbentQualitiesByStep: Map<number, StepArtifactQuality>;
  incumbentStreak: number;
  linkGaps: DeterministicStepGap[];
  pendingStepOrders: number[];
  linkSignalsByStep?: ReadonlyMap<number, readonly string[]>;
}): DeterministicPlaybookComposition {
  const plannedActions: PlannedBrowserAction[] = [];
  const carriedActions: CarriedCachedAction[] = [];
  const gaps: Array<{ stepOrder: number; reason: string }> = [];
  const decisions: StepDecision[] = [];
  const gapByStep = new Map<number, DeterministicStepGap>();
  for (const gap of args.linkGaps) {
    if (!gapByStep.has(gap.stepOrder)) gapByStep.set(gap.stepOrder, gap);
  }
  const pendingStepOrders = new Set(args.pendingStepOrders);
  const counts = {
    improved: 0,
    incumbent: 0,
    carry: 0,
    gap: 0,
    pending: pendingStepOrders.size,
    shadowCandidates: 0,
    shadowCandidateFailures: 0,
  };

  for (const step of [...args.playbookSteps].sort((a, b) => a.order - b.order)) {
    const stepSignals = args.linkSignalsByStep?.get(step.order);
    const carry = args.carryThroughActionsByStep.get(step.order);
    if (carry) {
      plannedActions.push(carry);
      counts.carry += 1;
      decisions.push({
        stepOrder: step.order,
        outcome: 'carry_through',
        detail: carry.actionType,
        ...(stepSignals && stepSignals.length > 0 ? { signals: [...stepSignals] } : {}),
      });
      continue;
    }

    const candidate = args.candidateArtifactsByStep.get(step.order);
    const incumbent = args.incumbentActionsByStep.get(step.order);
    const incumbentQuality = args.incumbentQualitiesByStep.get(step.order) ?? null;

    if (candidate) {
      const comparison = compareStepArtifacts(candidate.quality, incumbentQuality, {
        incumbentStreak: args.incumbentStreak,
      });
      if (comparison.verdict === 'improved') {
        const signals = mergedSignals(stepSignals, candidate.signals);
        plannedActions.push(candidate.plannedAction);
        counts.improved += 1;
        decisions.push({
          stepOrder: step.order,
          outcome: 'improved',
          layer: candidate.layer,
          reason: comparison.reason,
          ...(candidate.reason ? { detail: candidate.reason } : {}),
          ...(signals ? { signals } : {}),
        });
        continue;
      }
      if (incumbent) {
        const signals = mergedSignals(stepSignals, candidate.signals);
        carriedActions.push({ stepOrder: step.order, action: incumbent });
        counts.incumbent += 1;
        decisions.push({
          stepOrder: step.order,
          outcome: 'incumbent_kept',
          reason: comparison.reason,
          ...(signals ? { signals } : {}),
        });
        continue;
      }
      const signals = mergedSignals(stepSignals, candidate.signals);
      const reason = formatStructuredGapReason({
        code: comparison.reason,
        detail: `candidate verdict ${comparison.verdict} and no incumbent action could be copied`,
      });
      gaps.push({ stepOrder: step.order, reason });
      counts.gap += 1;
      decisions.push({
        stepOrder: step.order,
        outcome: 'gap',
        reason: comparison.reason,
        detail: reason,
        ...(signals ? { signals } : {}),
      });
      continue;
    }

    if (incumbent) {
      carriedActions.push({ stepOrder: step.order, action: incumbent });
      counts.incumbent += 1;
      decisions.push({
        stepOrder: step.order,
        outcome: 'incumbent_kept',
        reason: 'CMP_NOT_BETTER',
        ...(stepSignals && stepSignals.length > 0 ? { signals: [...stepSignals] } : {}),
      });
      continue;
    }

    const gap = gapByStep.get(step.order);
    const gapSignals = mergedSignals(stepSignals, gap?.signals);
    const pending = pendingStepOrders.has(step.order);
    if (pending) {
      const reason = formatStructuredGapReason({
        code: 'LINK_AMBIGUOUS_CANDIDATES',
        detail: gap?.detail ?? step.target,
      });
      decisions.push({
        stepOrder: step.order,
        outcome: 'pending',
        reason: 'LINK_AMBIGUOUS_CANDIDATES',
        detail: reason,
        ...(gapSignals ? { signals: gapSignals } : {}),
      });
      continue;
    }
    const code: StepDecisionReasonCode = gap?.reason ?? 'LINK_NO_MUTATION_FOR_STEP';
    const reason = formatStructuredGapReason({
      code,
      detail: gap?.detail ?? step.target,
    });
    gaps.push({ stepOrder: step.order, reason });
    counts.gap += 1;
    decisions.push({
      stepOrder: step.order,
      outcome: 'gap',
      reason: code,
      detail: reason,
      ...(gapSignals ? { signals: gapSignals } : {}),
    });
  }

  decisions.sort((a, b) => a.stepOrder - b.stepOrder);
  return {
    playbook: {
      playbookSteps: args.playbookSteps,
      plannedActions,
      ...(carriedActions.length > 0 ? { carriedActions } : {}),
      gaps,
    },
    decisionLog: {
      nodeId: args.nodeId,
      decisions,
      linkStats: buildLinkStats(decisions),
    },
    counts,
  };
}

export async function authorLinkedArtifacts(args: {
  linkResult: LinkResult;
  segmentActions: Array<{ index: number; action: TraceAction }>;
  steps: PlaybookStep[];
  cacheRow: DeterministicCacheRowLike | null;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  verifier: LocatorVerifier;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
  incumbentActionsByStep?: Map<number, CachedAction>;
  deps?: DeterministicAuthoringDeps;
}): Promise<DeterministicAuthoredArtifacts> {
  const stepByOrder = new Map(args.steps.map((step) => [step.order, step]));
  const actionByIndex = new Map(args.segmentActions.map((entry) => [entry.index, entry.action]));
  const linkSignals = linkSignalsByStepMap(args.linkResult);
  const artifactsByStep = new Map<number, DeterministicPlannedArtifact>();
  const gaps: DeterministicStepGap[] = [];

  for (const link of args.linkResult.links) {
    const step = stepByOrder.get(link.stepOrder);
    const action = actionByIndex.get(link.actionIndex);
    if (!step || !action) continue;
    if (isRunCodeAction(args.incumbentActionsByStep?.get(step.order))) {
      continue;
    }

    const authored = await authorLinkedStepArtifact({
      step,
      action,
      actionIndex: link.actionIndex,
      layer: link.layer,
      cacheRow: args.cacheRow,
      parsedRunCodeBySteps: args.parsedRunCodeBySteps,
      materializer: args.materializer,
      session: args.session,
      verifier: args.verifier,
      viewport: args.viewport,
      variableValues: args.variableValues,
      noPrimaryReason: 'LINK_ROW_SELECTOR_UNRESOLVED',
      linkSignals: linkSignals.get(step.order),
      deps: args.deps,
    });

    if (authored.ok) {
      artifactsByStep.set(step.order, authored.artifact);
    } else {
      gaps.push({
        stepOrder: step.order,
        reason: authored.reason,
        detail: authored.detail,
        ...(linkSignals.get(step.order) ? { signals: linkSignals.get(step.order)! } : {}),
      });
    }
  }

  return { artifactsByStep, gaps };
}

export async function attachRunCodeCarryShadowCandidates(args: {
  composition: DeterministicPlaybookComposition;
  linkResult: LinkResult;
  segmentActions: Array<{ index: number; action: TraceAction }>;
  steps: PlaybookStep[];
  cacheRow: DeterministicCacheRowLike | null;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  verifier: LocatorVerifier;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
  incumbentActionsByStep: Map<number, CachedAction>;
  deps?: DeterministicAuthoringDeps;
}): Promise<DeterministicPlaybookComposition> {
  const stepByOrder = new Map(args.steps.map((step) => [step.order, step]));
  const actionByIndex = new Map(args.segmentActions.map((entry) => [entry.index, entry.action]));
  const linkByStep = new Map(args.linkResult.links.map((link) => [link.stepOrder, link]));
  const decisions: StepDecision[] = [];
  let shadowCandidates = 0;
  let shadowCandidateFailures = 0;

  for (const decision of args.composition.decisionLog.decisions) {
    const next = { ...decision };
    const step = stepByOrder.get(decision.stepOrder);
    const link = linkByStep.get(decision.stepOrder);
    const incumbent = args.incumbentActionsByStep.get(decision.stepOrder);
    const action = link ? actionByIndex.get(link.actionIndex) : undefined;

    if (
      decision.outcome === 'incumbent_kept' &&
      !decision.shadowCandidate &&
      step &&
      link &&
      action &&
      elementTargetedStep(step.action) &&
      isRunCodeAction(incumbent)
    ) {
      next.shadowCandidate = await authorShadowCandidate({
        step,
        action,
        actionIndex: link.actionIndex,
        layer: link.layer,
        cacheRow: args.cacheRow,
        parsedRunCodeBySteps: args.parsedRunCodeBySteps,
        materializer: args.materializer,
        session: args.session,
        verifier: args.verifier,
        viewport: args.viewport,
        variableValues: args.variableValues,
        deps: args.deps,
      });
      if ('success' in next.shadowCandidate) {
        shadowCandidates += 1;
      } else {
        shadowCandidateFailures += 1;
      }
    }

    decisions.push(next);
  }

  return {
    playbook: args.composition.playbook,
    decisionLog: {
      ...args.composition.decisionLog,
      decisions,
      linkStats: buildLinkStats(decisions),
    },
    counts: {
      ...args.composition.counts,
      shadowCandidates: args.composition.counts.shadowCandidates + shadowCandidates,
      shadowCandidateFailures:
        args.composition.counts.shadowCandidateFailures + shadowCandidateFailures,
    },
  };
}

async function authorShadowCandidate(args: {
  step: PlaybookStep;
  action: TraceAction;
  actionIndex: number;
  layer: 1 | 2 | 3 | 4;
  cacheRow: DeterministicCacheRowLike | null;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  verifier: LocatorVerifier;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
  deps?: DeterministicAuthoringDeps;
}): Promise<ShadowCandidate> {
  const authored = await authorLinkedStepArtifact({
    ...args,
    noPrimaryReason: 'GT_SIGNAL_CONFLICT',
  });
  if (!authored.ok) {
    return {
      failure: {
        failedReason: asGroundTruthReason(authored.reason),
        detail: authored.detail,
      },
    };
  }

  const marker = markerSummaryFromSignals(authored.discoverySignals);
  return {
    success: {
      predicate: serializeSelectorStrategy(authored.strategy),
      selectorAuthor: 'deterministic',
      verified: true,
      markerTag: marker.markerTag,
      ...(marker.markerText ? { markerText: marker.markerText } : {}),
      layer: args.layer,
    },
  };
}

type AuthorLinkedStepResult =
  | {
      ok: true;
      artifact: DeterministicPlannedArtifact;
      strategy: SelectorStrategy;
      discoverySignals?: string[];
    }
  | {
      ok: false;
      reason: StepDecisionReasonCode;
      detail: string;
    };

async function authorLinkedStepArtifact(args: {
  step: PlaybookStep;
  action: TraceAction;
  actionIndex: number;
  layer: 1 | 2 | 3 | 4;
  cacheRow: DeterministicCacheRowLike | null;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  verifier: LocatorVerifier;
  viewport?: TraceViewport;
  variableValues: Record<string, string>;
  noPrimaryReason: StepDecisionReasonCode;
  linkSignals?: readonly string[];
  deps?: DeterministicAuthoringDeps;
}): Promise<AuthorLinkedStepResult> {
  if (!elementTargetedStep(args.step.action)) {
    const signals = mergedSignals(args.linkSignals);
    return {
      ok: true,
      artifact: {
        stepOrder: args.step.order,
        plannedAction: plannedActionFromTrace({ step: args.step, action: args.action }),
        quality: qualityFromAuthoredArtifact({
          selectorAuthor: null,
          predicate: 'none',
          volatileClean: true,
        }),
        layer: args.layer,
        ...(signals ? { signals } : {}),
      },
      strategy: {},
    };
  }

  const primary =
    primaryForStep({
      row: args.cacheRow,
      parsedRunCodeBySteps: args.parsedRunCodeBySteps,
      stepOrder: args.step.order,
      variableValues: args.variableValues,
    }) ?? primaryFromTraceAction(args.action);
  if (!primary) {
    return {
      ok: false,
      reason: args.noPrimaryReason,
      detail: 'no row selector, parsed run_code locator chain, or trace selector available',
    };
  }

  const discover = args.deps?.discoverTarget ?? discoverTarget;
  const discovered = await discover({
    materializer: args.materializer,
    session: args.session,
    actionIndex: args.actionIndex,
    traceAction: traceActionWithPreview(args.action),
    primary,
    viewport: args.viewport,
  }).catch((error: unknown) => ({
    ok: false as const,
    reason: 'GT_SIGNAL_CONFLICT' as const,
    detail: formatUnknownError(error),
  }));
  if (!discovered.ok) {
    return {
      ok: false,
      reason: discovered.reason,
      detail: discovered.detail ?? '',
    };
  }

  const author = args.deps?.authorAndVerify ?? authorAndVerify;
  const authored = await author({
    ref: discovered.ref,
    substrateYaml: discovered.substrateYaml,
    domPath: discovered.domPath,
    variableValues: args.variableValues,
    verifier: args.verifier,
    session: args.session,
  }).catch((error: unknown) => ({
    ok: false as const,
    reason: 'VERIFY_FAILED_TARGET_MISMATCH' as const,
    detail: formatUnknownError(error),
  }));
  if (!authored.ok) {
    return {
      ok: false,
      reason: authored.reason,
      detail: authored.detail ?? '',
    };
  }

  const signals = mergedSignals(args.linkSignals, discovered.signals);
  return {
    ok: true,
    artifact: {
      stepOrder: args.step.order,
      plannedAction: plannedActionFromTrace({
        step: args.step,
        action: args.action,
        selectors: authored.strategy,
      }),
      quality: qualityFromAuthoredArtifact({
        selectorAuthor: 'deterministic',
        predicate: authored.predicate,
        volatileClean: authored.verification.volatileTokenClean,
      }),
      layer: args.layer,
      reason: discovered.confirmations.join(', '),
      ...(signals ? { signals } : {}),
    },
    strategy: authored.strategy,
    ...(discovered.signals && discovered.signals.length > 0
      ? { discoverySignals: discovered.signals }
      : {}),
  };
}

export function linkerMacroResolutionLog(
  log: MacroResolutionLog | undefined
): Array<{ resolved: string; expression: string }> | undefined {
  if (!log || log.size === 0) return undefined;
  return [...log.values()].map((entry) => ({
    expression: entry.expression,
    resolved: entry.resolved,
  }));
}

export function buildParsedRunCodeBySteps(args: {
  steps: PlaybookStep[];
  cacheRow: DeterministicCacheRowLike | null;
}): Map<number, StrictParsedRunCode> {
  const out = new Map<number, StrictParsedRunCode>();
  for (const step of args.steps) {
    if (typeof step.cacheCode === 'string' && step.cacheCode.trim()) {
      out.set(step.order, adaptParseRunCodeStrictResult(parseRunCodeStrict(step.cacheCode)));
    }
  }
  if (!args.cacheRow) return out;
  const incumbentActions = buildIncumbentArtifactsByStep(args.cacheRow, args.steps);
  for (const [stepOrder, action] of incumbentActions) {
    if (out.has(stepOrder)) continue;
    if (action.actionType !== 'run_code' || typeof action.code !== 'string' || !action.code.trim()) {
      continue;
    }
    out.set(stepOrder, adaptParseRunCodeStrictResult(parseRunCodeStrict(action.code)));
  }
  return out;
}

export function collectSegmentActionsForNode(args: {
  nodeId: string;
  segments: readonly NodeSegment[];
  segmentNodeIds: ReadonlyArray<string | null>;
}): Array<{ index: number; action: TraceAction }> {
  const out: Array<{ index: number; action: TraceAction }> = [];
  args.segments.forEach((segment, index) => {
    if (args.segmentNodeIds[index] !== args.nodeId) return;
    out.push(...segment.actions);
  });
  return out.sort((a, b) => a.index - b.index);
}

export function pickBestValidCacheRow(
  rows: readonly DeterministicCacheRowLike[]
): DeterministicCacheRowLike | null {
  let best: { row: DeterministicCacheRowLike; cachedStepCount: number; score: number } | null =
    null;
  for (const row of rows) {
    const cachedStepCount =
      row.compiledEntriesJson && row.compiledEntriesJson.length > 0
        ? row.compiledEntriesJson.filter((entry) => entry.type === 'cached').length
        : (row.actionsJson?.length ?? 0);
    const score = compilationScore(row);
    if (
      !best ||
      cachedStepCount > best.cachedStepCount ||
      (cachedStepCount === best.cachedStepCount && score > best.score)
    ) {
      best = { row, cachedStepCount, score };
    }
  }
  return best?.row ?? null;
}

export function cachedActions(row: DeterministicCacheRowLike): CachedAction[] {
  return (row.actionsJson ?? []).filter(isCachedAction);
}

export function buildRowBridge(
  row: DeterministicCacheRowLike
): { compiledEntries: StoredCompiledEntry[]; actions: CachedAction[] } | undefined {
  if (!row.compiledEntriesJson || row.compiledEntriesJson.length === 0) return undefined;
  return {
    compiledEntries: [...row.compiledEntriesJson],
    actions: cachedActions(row),
  };
}

export function buildIncumbentArtifactsByStep(
  row: DeterministicCacheRowLike,
  steps: readonly PlaybookStep[]
): Map<number, CachedAction> {
  const out = new Map<number, CachedAction>();
  const stepOrders = new Set(steps.map((step) => step.order));
  for (const entry of row.compiledEntriesJson ?? []) {
    if (entry.type !== 'cached' || !stepOrders.has(entry.stepOrder)) continue;
    const action = cachedActionForCompiledEntry(row, entry);
    if (action) out.set(entry.stepOrder, action);
  }
  return out;
}

export function cachedActionForCompiledEntry(
  row: DeterministicCacheRowLike,
  entry: Extract<StoredCompiledEntry, { type: 'cached' }>
): CachedAction | null {
  if (entry.actionIndices.length !== 1) return null;
  return cachedActions(row)[entry.actionIndices[0]!] ?? null;
}

export function buildIncumbentQualitiesByStep(
  incumbentActions: Map<number, CachedAction>,
  verified: number
): Map<number, StepArtifactQuality> {
  const out = new Map<number, StepArtifactQuality>();
  for (const [stepOrder, action] of incumbentActions) {
    out.set(stepOrder, qualityFromCachedAction(action, verified));
  }
  return out;
}

export function qualityFromCachedAction(
  action: CachedAction,
  verified: number
): StepArtifactQuality {
  const selectorAuthor =
    action.selectorAuthor === 'deterministic' || action.selectorAuthor === 'agentic'
      ? action.selectorAuthor
      : null;
  return {
    kind:
      action.actionType === 'run_code' || typeof action.code === 'string'
        ? 'run_code'
        : 'structured',
    requiresAgent: Boolean(action.requiresAgent),
    selectorAuthor,
    predicateRank: selectorAuthor ? 500 : 900,
    volatileClean: true,
    bindingLossless: true,
    crossRunScore: { verified, attempted: Math.max(verified, 1) },
  };
}

export function qualityFromAuthoredArtifact(args: {
  selectorAuthor: 'deterministic' | 'agentic' | null;
  predicate: string;
  volatileClean: boolean;
}): StepArtifactQuality {
  return {
    kind: 'structured',
    requiresAgent: false,
    selectorAuthor: args.selectorAuthor,
    predicateRank: derivePredicateRank(args.predicate),
    volatileClean: args.volatileClean,
    bindingLossless: true,
    crossRunScore: null,
  };
}

export function rowStrategyForStep(
  row: DeterministicCacheRowLike | null,
  stepOrder: number
): SelectorStrategy | null {
  if (!row) return null;
  const entry = (row.compiledEntriesJson ?? []).find(
    (candidate): candidate is Extract<StoredCompiledEntry, { type: 'cached' }> =>
      candidate.type === 'cached' && candidate.stepOrder === stepOrder
  );
  const action = entry ? cachedActionForCompiledEntry(row, entry) : null;
  return action?.selectors ?? null;
}

export function primaryForStep(args: {
  row: DeterministicCacheRowLike | null;
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>;
  stepOrder: number;
  variableValues: Record<string, string>;
}): PrimaryTarget | null {
  const rowStrategy = rowStrategyForStep(args.row, args.stepOrder);
  if (rowStrategy) {
    return { kind: 'row-strategy', strategy: rowStrategy, variableValues: args.variableValues };
  }
  const locatorChain = runCodeLocatorForStep(args.parsedRunCodeBySteps, args.stepOrder);
  return locatorChain ? { kind: 'runcode-chain', locatorChain } : null;
}

export function primaryFromTraceAction(action: TraceAction): PrimaryTarget | null {
  const selector = action.params?.selector;
  return typeof selector === 'string' && selector.trim()
    ? { kind: 'trace-selector', selector }
    : null;
}

export function elementTargetedStep(action: string): boolean {
  return ['click', 'toggle', 'fill', 'fill_form', 'select', 'hover', 'upload', 'drag'].includes(
    action
  );
}

export function traceActionWithPreview(action: TraceAction): TraceAction {
  if (!action.resolvedElementPreview) return action;
  const params = action.params ?? {};
  if (typeof params.resolvedElementPreview === 'string') return action;
  return {
    ...action,
    params: { ...params, resolvedElementPreview: action.resolvedElementPreview },
  };
}

export function actionTypeForStep(step: PlaybookStep): string {
  switch (step.action) {
    case 'fill':
      return 'type';
    case 'select':
      return 'select_option';
    case 'press_key':
      return 'press_key';
    case 'upload':
      return 'file_upload';
    case 'toggle':
      return 'click';
    default:
      return step.action;
  }
}

export function toolNameForStep(step: PlaybookStep): string {
  switch (step.action) {
    case 'click':
    case 'toggle':
      return 'browser_click';
    case 'fill':
      return 'browser_type';
    case 'fill_form':
      return 'browser_fill_form';
    case 'select':
      return 'browser_select_option';
    case 'navigate':
      return 'browser_navigate';
    case 'verify':
      return 'browser_assert';
    case 'capture':
      return 'browser_evaluate';
    case 'scroll':
      return 'browser_scroll';
    case 'screenshot':
      return 'browser_screenshot';
    case 'resize':
      return 'browser_resize';
    case 'upload':
      return 'browser_file_upload';
    case 'press_key':
      return 'browser_press_key';
    case 'hover':
      return 'browser_hover';
    case 'drag':
      return 'browser_drag';
    case 'python_execute':
      return 'python_execute';
    case 'extract_table':
      return 'browser_extract_table';
    default:
      return '';
  }
}

export function plannedActionFromTrace(args: {
  step: PlaybookStep;
  action: TraceAction;
  selectors?: SelectorStrategy;
}): PlannedBrowserAction {
  return {
    stepOrder: args.step.order,
    toolName: toolNameForStep(args.step),
    actionType: actionTypeForStep(args.step),
    args: argsFromTrace(args.step, args.action),
    ...(args.selectors ? { selectors: args.selectors } : {}),
    elementDescription: args.step.target,
  };
}

export function buildCarryThroughActions(
  item: DeterministicCarryThroughSource,
  linkResult: LinkResult
): Map<number, PlannedBrowserAction> {
  const out = new Map<number, PlannedBrowserAction>();
  const stepByOrder = new Map(item.playbookSteps.map((step) => [step.order, step]));
  for (const carry of linkResult.carryThrough) {
    const step = stepByOrder.get(carry.stepOrder);
    if (!step) continue;
    if (carry.channel === 'assertion') {
      const assertion = item.assertionSpecsByStepOrder?.[step.order];
      if (!assertion) continue;
      out.set(step.order, {
        stepOrder: step.order,
        toolName: 'browser_assert',
        actionType: 'assert',
        args: {
          type: assertion.type,
          condition: assertion.condition,
          ...(assertion.expected !== undefined ? { expected: assertion.expected } : {}),
          ...(assertion.strict !== undefined ? { strict: assertion.strict } : {}),
          ...(assertion.severity ? { severity: assertion.severity } : {}),
          ...(assertion.inlineAssertionId
            ? { inlineAssertionId: assertion.inlineAssertionId }
            : {}),
          ...(assertion.label ? { label: assertion.label } : {}),
        },
        elementDescription: step.target,
        assertion,
      });
      continue;
    }
    out.set(step.order, {
      stepOrder: step.order,
      toolName: toolNameForStep(step),
      actionType: actionTypeForStep(step),
      args: { ...(step.params ?? {}), ...(step.target ? { label: step.target } : {}) },
      elementDescription: step.target,
    });
  }
  return out;
}

function strictKindForParsedInteraction(
  interaction: RunCodeStrictParseResult['interactions'][number]
): StrictParsedRunCode['interactions'][number]['kind'] | null {
  switch (interaction.actionType as string) {
    case 'click':
      return interaction.doubleClick ? 'dblclick' : 'click';
    case 'check':
      return 'check';
    case 'uncheck':
      return 'uncheck';
    case 'type':
      return /\.fill\s*\(/.test(interaction.source) ? 'fill' : 'type';
    case 'select':
      return 'selectOption';
    case 'press-key':
      return 'press';
    case 'file-upload':
      return 'setInputFiles';
    case 'navigate':
      return 'goto';
    case 'drag':
      return 'dragTo';
    case 'hover':
      return 'hover';
    case 'scroll':
      return 'scroll';
    case 'move':
      return null;
    case 'wait':
      return null;
  }
  return null;
}

function compilationScore(row: DeterministicCacheRowLike): number {
  const raw = row.compilationMeta;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 0;
  const score = (raw as { compilationScore?: unknown }).compilationScore;
  return typeof score === 'number' ? score : 0;
}

function isCachedAction(value: unknown): value is CachedAction {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.index === 'number' &&
    typeof record.toolName === 'string' &&
    typeof record.actionType === 'string' &&
    typeof record.nodeId === 'string' &&
    (!record.args || (typeof record.args === 'object' && !Array.isArray(record.args)))
  );
}

function isRunCodeAction(action: CachedAction | undefined | null): boolean {
  return action?.actionType === 'run_code';
}

function asGroundTruthReason(reason: StepDecisionReasonCode): GroundTruthReasonCode {
  if (reason.startsWith('GT_') || reason.startsWith('AUTHOR_') || reason.startsWith('VERIFY_')) {
    return reason as GroundTruthReasonCode;
  }
  return 'GT_SIGNAL_CONFLICT';
}

function markerSummaryFromSignals(signals: string[] | undefined): {
  markerTag: string;
  markerText?: string;
} {
  const markerTag = firstSignalValue(signals, 'gt_marker_tag:') ?? 'unknown';
  const markerText = firstSignalValue(signals, 'gt_marker_text:');
  return {
    markerTag,
    ...(markerText ? { markerText } : {}),
  };
}

function firstSignalValue(signals: string[] | undefined, prefix: string): string | undefined {
  return signals?.find((signal) => signal.startsWith(prefix))?.slice(prefix.length);
}

function serializeSelectorStrategy(strategy: SelectorStrategy): string {
  return JSON.stringify(sortJsonValue(strategy));
}

function sortJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => sortJsonValue(entry));
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) {
    const child = record[key];
    if (child !== undefined) out[key] = sortJsonValue(child);
  }
  return out;
}

function formatUnknownError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function runCodeLocatorForStep(
  parsedRunCodeBySteps: Map<number, StrictParsedRunCode>,
  stepOrder: number
): string | null {
  const parsed = parsedRunCodeBySteps.get(stepOrder);
  const chain = parsed?.interactions.find((interaction) => interaction.locatorChain)?.locatorChain;
  return chain ?? null;
}

function argsFromTrace(step: PlaybookStep, action: TraceAction): Record<string, unknown> {
  const params = action.params ?? {};
  const out: Record<string, unknown> = {};
  const copyFirst = (target: string, keys: string[]): void => {
    for (const key of keys) {
      const value = params[key];
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        out[target] = value;
        return;
      }
      if (Array.isArray(value)) {
        out[target] = value.filter((entry): entry is string => typeof entry === 'string');
        return;
      }
    }
  };
  switch (step.action) {
    case 'navigate':
      copyFirst('url', ['url']);
      break;
    case 'fill':
      copyFirst('text', ['text', 'value']);
      break;
    case 'select':
      copyFirst('value', ['value', 'option', 'label', 'values', 'options']);
      break;
    case 'press_key':
      copyFirst('key', ['key']);
      break;
    case 'upload':
      copyFirst('path', ['path', 'file', 'files', 'localPaths']);
      break;
    default:
      break;
  }
  return step.target ? { ...out, label: step.target } : out;
}

function buildLinkStats(decisions: StepDecision[]): NodeDecisionLog['linkStats'] {
  const byLayer: Record<string, number> = {};
  const gapsByReason: Record<string, number> = {};
  for (const decision of decisions) {
    if (decision.outcome === 'improved' && decision.layer !== undefined) {
      const key = String(decision.layer);
      byLayer[key] = (byLayer[key] ?? 0) + 1;
    }
    if (decision.outcome === 'gap' && decision.reason) {
      gapsByReason[decision.reason] = (gapsByReason[decision.reason] ?? 0) + 1;
    }
  }
  return { byLayer, gapsByReason };
}
